import React from "react";
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftBadge from "components/SoftBadge";

function FooterWithDividers({ children, gap = 1.25 }) {
  const items = React.Children.toArray(children).filter(Boolean);
  if (!items.length) return null;

  return (
    <SoftBox display="flex" alignItems="center" gap={gap} minWidth={0} sx={{ overflow: "hidden" }}>
      {items.map((child, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <SoftBox
              sx={{
                width: "1px",
                height: 26,
                background: "rgba(233,236,239,0.95)",
                flex: "0 0 auto",
              }}
            />
          )}
          <SoftBox sx={{ minWidth: 0, flex: "0 0 auto" }}>{child}</SoftBox>
        </React.Fragment>
      ))}
    </SoftBox>
  );
}

export default function SoftUITimeTableShellVerticalFrozen({
  rows = [],
  timestamps = [],

  height,

  // sizing
  leftColWidth = 160,
  colWidth = 120,
  totalColWidth = 140,

  // timestamp formatting
  formatTimestamp,

  // value rendering
  getCellValue,
  formatValue,
  valueColor,

  // totals
  showTotalColumn = false,
  formatTotal,
  totalColor,

  // optional total row (bottom): total per row-column across all timestamps
  showTotalRow = false,

  // top floating bar
  footerItems,
  footerHeight = 56,
  footerFloatPadding = 16,

  // text size toggle
  defaultTextSize = "small",
  onTextSizeChange,

  // timestamp order toggle
  defaultTimestampOrder = "asc",
  timestampOrder,
  onTimestampOrderChange,

  // visible timestamp count toggle
  defaultVisibleTimestampCount = "all",
  visibleTimestampCount,
  onVisibleTimestampCountChange,
  visibleTimestampCountOptions = [10, 25, 50, "all"],

  // interval handling
  ignoreFirstTimestampRow = true,

  // size tuning
  smallValueFontSize = "0.95rem",
  bigValueFontSize = "1.55rem",
  smallRowHeight = 44,
  bigRowHeight = 72,

  // header height
  headerHeight = 56,

  // row label rendering overrides
  renderLeftTitle,
  renderLeftSubtitle,

  // hover callback
  onHoverTimePeriod,

  sx,
}) {
  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  const [internalTextSize, setInternalTextSize] = React.useState(defaultTextSize);
  const textSize = onTextSizeChange ? defaultTextSize : internalTextSize;

  const setTextSize = React.useCallback(
    (next) => {
      if (onTextSizeChange) onTextSizeChange(next);
      else setInternalTextSize(next);
    },
    [onTextSizeChange]
  );

  const [internalTimestampOrder, setInternalTimestampOrder] = React.useState(defaultTimestampOrder);
  const resolvedTimestampOrder = onTimestampOrderChange ? timestampOrder ?? defaultTimestampOrder : internalTimestampOrder;

  const setResolvedTimestampOrder = React.useCallback(
    (next) => {
      if (onTimestampOrderChange) onTimestampOrderChange(next);
      else setInternalTimestampOrder(next);
    },
    [onTimestampOrderChange]
  );

  const [internalVisibleTimestampCount, setInternalVisibleTimestampCount] = React.useState(defaultVisibleTimestampCount);
  const resolvedVisibleTimestampCount = onVisibleTimestampCountChange
    ? visibleTimestampCount ?? defaultVisibleTimestampCount
    : internalVisibleTimestampCount;

  const setResolvedVisibleTimestampCount = React.useCallback(
    (next) => {
      if (onVisibleTimestampCountChange) onVisibleTimestampCountChange(next);
      else setInternalVisibleTimestampCount(next);
    },
    [onVisibleTimestampCountChange]
  );

  const isBig = textSize === "big";

  const toTsKeySeconds = React.useCallback((t) => {
    const n = Number(t);
    if (!Number.isFinite(n)) return String(t);
    const seconds = n >= 1e12 ? Math.floor(n / 1000) : Math.floor(n);
    return String(seconds);
  }, []);

  const allTsKeysAsc = React.useMemo(() => {
    const keys = (timestamps || []).map(toTsKeySeconds);
    const uniq = Array.from(new Set(keys));

    uniq.sort((a, b) => {
      const na = Number(a);
      const nb = Number(b);
      if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
      return String(a).localeCompare(String(b));
    });

    return uniq;
  }, [timestamps, toTsKeySeconds]);

  const baseTsKeys = React.useMemo(() => {
    if (!ignoreFirstTimestampRow) return allTsKeysAsc;
    if (allTsKeysAsc.length <= 1) return [];
    return allTsKeysAsc.slice(1);
  }, [allTsKeysAsc, ignoreFirstTimestampRow]);

  const allTsKeys = React.useMemo(() => {
    return resolvedTimestampOrder === "desc" ? [...baseTsKeys].reverse() : baseTsKeys;
  }, [baseTsKeys, resolvedTimestampOrder]);

  const tsKeys = React.useMemo(() => {
    if (resolvedVisibleTimestampCount === "all") return allTsKeys;

    const limit = Number(resolvedVisibleTimestampCount);
    if (!Number.isFinite(limit) || limit <= 0) return allTsKeys;

    return allTsKeys.slice(0, limit);
  }, [allTsKeys, resolvedVisibleTimestampCount]);

  const getPreviousTs = React.useCallback(
    (ts) => {
      const idx = allTsKeysAsc.indexOf(ts);
      if (idx <= 0) return null;
      return allTsKeysAsc[idx - 1];
    },
    [allTsKeysAsc]
  );

  const getHoverTimePeriod = React.useCallback(
    (ts) => {
      const prevTs = getPreviousTs(ts);
      const endNum = Number(ts);
      const startNum = Number(prevTs);

      if (!Number.isFinite(endNum) || !Number.isFinite(startNum)) {
        return {
          startTimestamp: Number.isFinite(startNum) ? startNum : null,
          duration: null,
          endTimestamp: Number.isFinite(endNum) ? endNum : null,
          ts,
        };
      }

      return {
        startTimestamp: startNum,
        duration: Math.max(0, endNum - startNum),
        endTimestamp: endNum,
        ts,
      };
    },
    [getPreviousTs]
  );

  const defaultFormatSingleTimestamp = React.useCallback((ts) => {
    const n = Number(ts);
    if (!Number.isFinite(n)) return String(ts);
    const d = new Date(n * 1000);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }, []);

  const fmtTs = React.useCallback(
    (ts) => {
      const prevTs = getPreviousTs(ts);

      if (formatTimestamp) return formatTimestamp(ts, prevTs);
      if (!prevTs) return defaultFormatSingleTimestamp(ts);

      return `${defaultFormatSingleTimestamp(prevTs)} - ${defaultFormatSingleTimestamp(ts)}`;
    },
    [formatTimestamp, getPreviousTs, defaultFormatSingleTimestamp]
  );

  const fmtVal =
    formatValue ||
    ((v) => {
      if (v === null || v === undefined) return "";
      const n = Number(v);
      if (Number.isFinite(n)) return n.toLocaleString();
      return String(v);
    });

  const valColor =
    valueColor ||
    ((v) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return "dark";
      return n > 0 ? "dark" : n < 0 ? "dark" : "dark";
    });

  const fmtTotal =
    formatTotal ||
    ((v) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return "";
      return n.toLocaleString();
    });

  const totalValColor =
    totalColor ||
    ((v) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return "dark";
      return n > 0 ? "success" : n < 0 ? "error" : "dark";
    });

  const getVal =
    getCellValue ||
    ((row, ts) => {
      return row?.values?.[ts];
    });

  const getRowTotal = React.useCallback(
    (row) => tsKeys.reduce((sum, ts) => sum + toNum(getVal(row, ts)), 0),
    [tsKeys, getVal]
  );

  const getTimestampTotal = React.useCallback(
    (ts) => rows.reduce((sum, row) => sum + toNum(getVal(row, ts)), 0),
    [rows, getVal]
  );

  const grandTotal = React.useMemo(() => rows.reduce((s, r) => s + getRowTotal(r), 0), [rows, getRowTotal]);

  const exportCsv = React.useCallback(() => {
    const safe = (s) => {
      const str = String(s ?? "");
      const needs = /[",\n]/.test(str);
      return needs ? `"${str.replace(/"/g, '""')}"` : str;
    };

    const colLabels = rows.map((row) => row?.label ?? row?.id ?? "");
    const header = ["timestamp", ...colLabels, ...(showTotalColumn ? ["Total"] : [])];

    const lines = [];
    lines.push(header.map(safe).join(","));

    for (const ts of tsKeys) {
      const base = [
        fmtTs(ts),
        ...rows.map((row) => {
          const v = getVal(row, ts);
          const n = Number(v);
          if (Number.isFinite(n)) return n;
          return v ?? "";
        }),
        ...(showTotalColumn ? [getTimestampTotal(ts)] : []),
      ];
      lines.push(base.map(safe).join(","));
    }

    if (showTotalRow) {
      const row = [
        "Total",
        ...rows.map((r) => getRowTotal(r)),
        ...(showTotalColumn ? [grandTotal] : []),
      ];
      lines.push(row.map(safe).join(","));
    }

    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `time-table-vertical_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [rows, tsKeys, fmtTs, getVal, showTotalColumn, showTotalRow, getRowTotal, getTimestampTotal, grandTotal]);

  const footerNode =
    typeof footerItems === "function"
      ? footerItems({ rows, tsKeys, exportCsv, allTsKeys })
      : footerItems ?? null;

  const HEADER_H = headerHeight;
  const ROW_H = isBig ? bigRowHeight : smallRowHeight;

  const valueSxExtra = {
    fontSize: isBig ? bigValueFontSize : smallValueFontSize,
    lineHeight: 1.05,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  };

  const minCenterWidth = rows.length * colWidth + 24;
  const tableMinWidth = leftColWidth + minCenterWidth + (showTotalColumn ? totalColWidth : 0);

  const flexibleColumnsTemplate = React.useMemo(
    () => rows.map(() => `minmax(${colWidth}px, 1fr)`).join(" "),
    [rows.length, colWidth]
  );

  const centerGridSx = {
    display: "grid",
    gridTemplateColumns: flexibleColumnsTemplate || `minmax(${colWidth}px, 1fr)`,
    alignItems: "center",
    minWidth: minCenterWidth,
    width: "100%",
  };

  const headerCommonOuter = {
    position: "sticky",
    top: 0,
    zIndex: 20,
    height: HEADER_H,
    boxSizing: "border-box",
    border: "1px solid rgba(233,236,239,0.9)",
    background: "rgba(248,249,250,0.98)",
    backdropFilter: "blur(10px)",
    display: "flex",
    alignItems: "stretch",
    py: 0,
    px: 0,
  };

  const headerInner = {
    height: "100%",
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    px: 1.5,
    py: 1.25,
    overflow: "hidden",
    minWidth: 0,
  };

  const headerLeftOuter = {
    ...headerCommonOuter,
    left: 0,
    zIndex: 23,
    borderRadius: "12px 0 0 12px",
    borderRight: "none",
  };

  const headerRightOuter = {
    ...headerCommonOuter,
    zIndex: 21,
    borderRadius: showTotalColumn ? 0 : "0 12px 12px 0",
  };

  const totalHeaderOuter = {
    ...headerCommonOuter,
    right: 0,
    zIndex: 24,
    borderRadius: "0 12px 12px 0",
    borderLeft: "none",
  };

  const rowOuterLeft = () => ({
    position: "sticky",
    left: 0,
    zIndex: 10,
    height: ROW_H,
    boxSizing: "border-box",
    borderRadius: "14px 0 0 14px",
    border: "1px solid rgba(233,236,239,0.85)",
    borderRight: "none",
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 10px 26px rgba(0,0,0,0.04)",
    py: 0,
    px: 0,
    display: "flex",
    alignItems: "stretch",
  });

  const rowOuterRight = (isLastRow = false) => ({
    position: "relative",
    zIndex: 1,
    height: ROW_H,
    boxSizing: "border-box",
    borderRadius: isLastRow && !showTotalColumn ? "0 14px 14px 0" : showTotalColumn ? 0 : "0 14px 14px 0",
    border: "1px solid rgba(233,236,239,0.85)",
    background: "rgba(255,255,255,0.72)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 10px 26px rgba(0,0,0,0.04)",
    py: 0,
    px: 0,
    display: "flex",
    alignItems: "stretch",
    transition: "transform 0.12s ease, background 0.12s ease",
    "&:hover": {
      background: "rgba(189, 189, 189, 0.06)",
      transform: "translateY(-1px)",
    },
  });

  const rowOuterTotal = () => ({
    position: "sticky",
    right: 0,
    zIndex: 11,
    height: ROW_H,
    boxSizing: "border-box",
    borderRadius: "0 14px 14px 0",
    border: "1px solid rgba(233,236,239,0.85)",
    borderLeft: "none",
    background: "rgba(255,255,255,0.97)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 10px 26px rgba(0,0,0,0.04)",
    py: 0,
    px: 0,
    display: "flex",
    alignItems: "stretch",
  });

  const rowInner = {
    height: "100%",
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    px: 1.5,
    py: isBig ? 1.1 : 0.6,
    overflow: "hidden",
    minWidth: 0,
  };

  const headerTextSx = { whiteSpace: "nowrap", lineHeight: 1, display: "block" };

  const topBarWrapSx = {
    flex: "0 0 auto",
    padding: `${footerFloatPadding}px`,
    paddingBottom: 0,
  };

  const topBarPanelSx = {
    height: footerHeight,
    borderRadius: "12px",
    border: "1px solid rgba(233,236,239,0.9)",
    background: "rgba(248,249,250,0.96)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 10px 26px rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 1.5,
    boxSizing: "border-box",
    minWidth: 0,
    gap: 1.25,
  };

  const ToggleButton = ({ active, children, onClick, minWidth = 72 }) => (
    <SoftButton
      variant={active ? "contained" : "outlined"}
      color="secondary"
      size="small"
      onClick={onClick}
      sx={{
        height: 34,
        borderRadius: "12px",
        px: 1.25,
        minWidth,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </SoftButton>
  );

  const renderTitle = renderLeftTitle || ((row) => row?.label ?? "");
  const renderSubtitle =
    renderLeftSubtitle ||
    ((row) => (
      <SoftBox display="flex" gap={1} alignItems="center" mt={0.5}>
        {row?.group && <SoftBadge variant="contained" color="secondary" badgeContent={row.group} container />}
        {row?.subtitleLeft}
      </SoftBox>
    ));

  return (
    <Card sx={{ height, display: "flex", flexDirection: "column", overflow: "hidden", ...sx }}>
      <SoftBox sx={topBarWrapSx}>
        <SoftBox sx={topBarPanelSx}>
          <FooterWithDividers>
            {footerNode}

            <SoftBox display="flex" alignItems="center" gap={0.75}>
              <ToggleButton active={!isBig} onClick={() => setTextSize("small")}>
                Small
              </ToggleButton>
              <ToggleButton active={isBig} onClick={() => setTextSize("big")}>
                Big
              </ToggleButton>
            </SoftBox>

            <SoftBox display="flex" alignItems="center" gap={0.75}>
              <ToggleButton
                active={resolvedTimestampOrder === "asc"}
                onClick={() => setResolvedTimestampOrder("asc")}
                minWidth={96}
              >
                Ascending
              </ToggleButton>
              <ToggleButton
                active={resolvedTimestampOrder === "desc"}
                onClick={() => setResolvedTimestampOrder("desc")}
                minWidth={104}
              >
                Descending
              </ToggleButton>
            </SoftBox>

            {/* <SoftBox display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
              {visibleTimestampCountOptions.map((option) => {
                const isAll = option === "all";
                const label = isAll ? "Show All" : `Show ${option}`;
                const active = resolvedVisibleTimestampCount === option;

                return (
                  <ToggleButton
                    key={`show-${option}`}
                    active={active}
                    onClick={() => setResolvedVisibleTimestampCount(option)}
                    minWidth={isAll ? 98 : 92}
                  >
                    {label}
                  </ToggleButton>
                );
              })}
            </SoftBox> */}

            <SoftButton
              variant="outlined"
              color="secondary"
              size="small"
              onClick={exportCsv}
              sx={{
                height: 34,
                borderRadius: "12px",
                px: 1.5,
                whiteSpace: "nowrap",
                minWidth: 0,
                flex: "0 0 auto",
              }}
            >
              Export CSV
            </SoftButton>
          </FooterWithDividers>
        </SoftBox>
      </SoftBox>

      <SoftBox
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          overflow: "auto",
          px: 2,
          py: 2,
          pt: 2,
        }}
      >
        <SoftBox
          sx={{
            display: "grid",
            gridTemplateColumns: showTotalColumn
              ? `${leftColWidth}px minmax(${minCenterWidth}px, 1fr) ${totalColWidth}px`
              : `${leftColWidth}px minmax(${minCenterWidth}px, 1fr)`,
            alignItems: "start",
            columnGap: 0,
            minWidth: tableMinWidth,
            width: "100%",
          }}
        >
          <SoftBox sx={headerLeftOuter}>
            <SoftBox sx={headerInner}>
              <SoftTypography variant="caption" fontWeight="bold" color="dark" sx={headerTextSx}>
                Time
              </SoftTypography>
            </SoftBox>
          </SoftBox>

          <SoftBox sx={headerRightOuter}>
            <SoftBox sx={{ ...headerInner, px: 0 }}>
              <SoftBox sx={{ ...centerGridSx, px: 1.5 }}>
                {rows.map((row, idx) => {
                  const colour = row?.colour ?? "#CBD5E1";
                  return (
                    <SoftBox
                      key={`CH:${row?.id ?? idx}`}
                      sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: 1.25, px: 1.5 }}
                    >
                      <SoftBox
                        sx={{
                          width: "4px",
                          height: "34px",
                          borderRadius: "10px",
                          background: colour,
                          opacity: 0.95,
                          flex: "0 0 auto",
                        }}
                      />
                      <SoftBox
                        sx={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "999px",
                          background: colour,
                          boxShadow: "0 0 0 3px rgba(255,255,255,0.9)",
                          border: "1px solid rgba(0,0,0,0.06)",
                          flex: "0 0 auto",
                        }}
                      />
                      <SoftBox minWidth={0} flex={1}>
                        <SoftTypography
                          variant="button"
                          fontWeight="bold"
                          color="dark"
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "block",
                            lineHeight: 1.1,
                          }}
                          title={row?.label ?? row?.id ?? ""}
                        >
                          {renderTitle(row)}
                        </SoftTypography>
                        {renderSubtitle(row)}
                      </SoftBox>
                    </SoftBox>
                  );
                })}
              </SoftBox>
            </SoftBox>
          </SoftBox>

          {showTotalColumn && (
            <SoftBox sx={totalHeaderOuter}>
              <SoftBox sx={{ ...headerInner, justifyContent: "flex-end" }}>
                <SoftTypography variant="caption" fontWeight="bold" color="dark" textAlign="right" sx={headerTextSx}>
                  Total
                </SoftTypography>
              </SoftBox>
            </SoftBox>
          )}

          {tsKeys.map((ts) => {
            const hoverPayload = getHoverTimePeriod(ts);
            const hoverHandlers = onHoverTimePeriod
              ? {
                  onMouseEnter: () => onHoverTimePeriod(hoverPayload),
                  onMouseLeave: () => onHoverTimePeriod(null),
                }
              : {};

            return (
              <React.Fragment key={`ROW:${ts}`}>
                <SoftBox mb={0.75} sx={rowOuterLeft()} {...hoverHandlers}>
                  <SoftBox sx={rowInner}>
                    <SoftTypography
                      variant="button"
                      fontWeight="bold"
                      color="dark"
                      sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}
                      title={String(ts)}
                    >
                      {fmtTs(ts)}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>

                <SoftBox mb={0.75} sx={rowOuterRight()} {...hoverHandlers}>
                  <SoftBox sx={{ ...rowInner, px: 0 }}>
                    <SoftBox sx={{ ...centerGridSx, px: 1.5 }}>
                      {rows.map((row, idx) => {
                        const raw = getVal(row, ts);
                        const content = fmtVal(raw, row, ts);
                        const colour = valColor(raw, row, ts);

                        return (
                          <SoftBox
                            key={`C:${row?.id ?? idx}:${ts}`}
                            sx={{
                              minWidth: 0,
                              px: 1.5,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <SoftTypography
                              variant="button"
                              fontWeight="bold"
                              color={colour}
                              textAlign="left"
                              sx={valueSxExtra}
                            >
                              {content}
                            </SoftTypography>
                          </SoftBox>
                        );
                      })}
                    </SoftBox>
                  </SoftBox>
                </SoftBox>

                {showTotalColumn && (
                  <SoftBox mb={0.75} sx={rowOuterTotal()} {...hoverHandlers}>
                    <SoftBox sx={{ ...rowInner, justifyContent: "flex-end" }}>
                      <SoftTypography
                        variant="button"
                        fontWeight="bold"
                        color={totalValColor(getTimestampTotal(ts))}
                        textAlign="right"
                        sx={valueSxExtra}
                      >
                        {fmtTotal(getTimestampTotal(ts))}
                      </SoftTypography>
                    </SoftBox>
                  </SoftBox>
                )}
              </React.Fragment>
            );
          })}

          {showTotalRow && (
            <>
              <SoftBox mb={0.75} sx={rowOuterLeft()}>
                <SoftBox sx={rowInner}>
                  <SoftTypography variant="button" fontWeight="bold" color="dark">
                    Total
                  </SoftTypography>
                </SoftBox>
              </SoftBox>

              <SoftBox mb={0.75} sx={rowOuterRight(true)}>
                <SoftBox sx={{ ...rowInner, px: 0 }}>
                  <SoftBox sx={{ ...centerGridSx, px: 1.5 }}>
                    {rows.map((row, idx) => {
                      const total = getRowTotal(row);
                      return (
                        <SoftBox
                          key={`RT:${row?.id ?? idx}`}
                          sx={{
                            minWidth: 0,
                            px: 1.5,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <SoftTypography
                            variant="button"
                            fontWeight="bold"
                            color={totalValColor(total, row)}
                            textAlign="right"
                            sx={valueSxExtra}
                          >
                            {fmtTotal(total, row)}
                          </SoftTypography>
                        </SoftBox>
                      );
                    })}
                  </SoftBox>
                </SoftBox>
              </SoftBox>

              {showTotalColumn && (
                <SoftBox mb={0.75} sx={rowOuterTotal()}>
                  <SoftBox sx={{ ...rowInner, justifyContent: "flex-end" }}>
                    <SoftTypography
                      variant="button"
                      fontWeight="bold"
                      color={totalValColor(grandTotal)}
                      textAlign="right"
                      sx={valueSxExtra}
                    >
                      {fmtTotal(grandTotal)}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
              )}
            </>
          )}
        </SoftBox>
      </SoftBox>
    </Card>
  );
}